import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  getMaintenanceChipSx,
  getMaintenancePriorityMeta,
  resolveRoomMaintenanceSummary,
} from '../../utils/maintenanceRoomUtils';

function MaintenanceTooltipContent({ requests, meta }) {
  return (
    <Box sx={{ maxWidth: 260 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.75 }}>
        Open maintenance · {meta.label} priority
      </Typography>
      <Stack spacing={0.5}>
        {requests.slice(0, 4).map((req) => (
          <Typography key={req.id} variant="caption" sx={{ display: 'block', lineHeight: 1.35 }}>
            • {req.issue_type || 'Issue'}
            {req.priority ? ` (${String(req.priority).toLowerCase()})` : ''}
          </Typography>
        ))}
        {requests.length > 4 ? (
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            +{requests.length - 4} more
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function MaintenanceRoomIndicator({
  room,
  isDark = true,
  variant = 'chip',
  compact = false,
}) {
  const summary = resolveRoomMaintenanceSummary(room);
  if (!summary.hasMaintenance || !summary.priority) return null;

  const { requests, count, priority, meta, topIssue } = summary;
  const chipLabel = compact
    ? meta.label
    : count > 1
      ? `${meta.label} · ${count}`
      : meta.label;

  if (variant === 'icon') {
    const iconSize = compact ? 12 : 16;
    const buttonSize = compact ? 20 : 28;
    return (
      <Tooltip title={<MaintenanceTooltipContent requests={requests} meta={meta} />} arrow>
        <IconButton
          size="small"
          aria-label={`Maintenance ${meta.label} priority`}
          onClick={(event) => event.stopPropagation()}
          sx={{
            width: buttonSize,
            height: buttonSize,
            p: 0,
            flexShrink: 0,
            color: meta.color,
            bgcolor: isDark ? alpha(meta.color, 0.16) : alpha(meta.color, 0.1),
            border: `1px solid ${alpha(meta.color, isDark ? 0.45 : 0.35)}`,
            boxShadow: `0 0 10px ${alpha(meta.color, 0.28)}`,
            '&:hover': {
              bgcolor: isDark ? alpha(meta.color, 0.26) : alpha(meta.color, 0.18),
            },
          }}
        >
          {priority === 'HIGH' ? (
            <PriorityHighIcon sx={{ fontSize: iconSize }} />
          ) : (
            <BuildCircleIcon sx={{ fontSize: iconSize }} />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  if (variant === 'dot') {
    return (
      <Tooltip title={<MaintenanceTooltipContent requests={requests} meta={meta} />} arrow>
        <Box
          sx={{
            width: compact ? 8 : 10,
            height: compact ? 8 : 10,
            borderRadius: '50%',
            background: meta.gradient,
            boxShadow: `0 0 0 2px ${alpha(meta.color, 0.25)}, 0 0 10px ${meta.glow}`,
            flexShrink: 0,
          }}
        />
      </Tooltip>
    );
  }

  if (variant === 'banner') {
    return (
      <Tooltip title={<MaintenanceTooltipContent requests={requests} meta={meta} />} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.55,
            borderRadius: '10px',
            background: isDark
              ? `linear-gradient(90deg, ${alpha(meta.color, 0.22)} 0%, ${alpha(meta.color, 0.08)} 100%)`
              : `linear-gradient(90deg, ${alpha(meta.color, 0.14)} 0%, ${alpha(meta.color, 0.05)} 100%)`,
            border: `1px solid ${alpha(meta.color, isDark ? 0.45 : 0.35)}`,
          }}
        >
          <BuildCircleIcon sx={{ fontSize: compact ? 14 : 16, color: meta.color }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 800,
                color: isDark ? '#fff' : '#1e293b',
                fontSize: compact ? '0.62rem' : '0.68rem',
                lineHeight: 1.2,
              }}
            >
              Maintenance · {meta.label}
            </Typography>
            {topIssue ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: isDark ? alpha('#fff', 0.78) : '#475569',
                  fontSize: compact ? '0.58rem' : '0.62rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {topIssue}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={<MaintenanceTooltipContent requests={requests} meta={meta} />} arrow>
      <Chip
        size="small"
        icon={
          priority === 'HIGH'
            ? <PriorityHighIcon sx={{ fontSize: compact ? 13 : 15 }} />
            : <BuildCircleIcon sx={{ fontSize: compact ? 13 : 15 }} />
        }
        label={chipLabel}
        sx={getMaintenanceChipSx(priority, isDark, { compact })}
      />
    </Tooltip>
  );
}

export { MaintenanceTooltipContent, getMaintenancePriorityMeta };
